import Accordion from "@/common/components/Accordion"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import Checkbox from "@/common/components/Checkbox"
import MaskedTextField from "@/common/components/MaskedTextField"
import SelectController from "@/common/components/SelectController"
import Spinner from "@/common/components/Spinner"
import TextField from "@/common/components/TextField"
import { percentageMaskAllowNegative } from "@/common/constants/masks.constant"
import { useProductCategories } from "@/common/hooks/queries/useProductCategories"
import { useProducts } from "@/common/hooks/queries/useProducts"
import useDebounce from "@/common/hooks/useDebounce"
import { Mapper } from "@/common/services/mapper"
import { useCreateFeeTemplate } from "@/features/feeTemplates/hooks/useCreateFeeTemplate"
import { useFeeTemplateOptions } from "@/features/feeTemplates/hooks/useFeeTemplateOptions"
import { ProductCategoryData } from "@/features/productCategories/types/productCategories"
import { ProductData } from "@/features/products/types/product"
import { zodResolver } from "@hookform/resolvers/zod"
import { ChevronsRight } from "lucide-react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { options as applicableEntityOptions } from "../../constants/applicableEntity.constant"
import { options as calculateAsOptions } from "../../constants/calculateAs.constant"
import { options as feeTemplateKindOptions } from "../../constants/feeTemplateKind.constant"
import { options as paymentModalityOptions } from "../../constants/paymentModality.constant"
import { FeeTemplateValidationSchema, createSchema } from "../../schemas/create.schema"
import { FormTransformers } from "@/common/utils/formTransformers"
import { useQueryClient } from "@tanstack/react-query"
import { CurrencyTextField } from "@/common/components/CurrencyTextField"
import { useGoBack } from "@/common/hooks/useGoBack";
import GoBackButton from "@/common/components/GoBackButton";
import { useBillingCaptureMethods } from "@/common/hooks/queries/useBillingCaptureMethods";
import { BillingCaptureMethod } from "@/common/types/establishment/billingCaptureMethod";
import { applicableEstablishmentCommercialKindOptions } from "../../constants/applicableEstablishmentCommercialKind.constant";
import useUserPermissions from "@/common/hooks/useUserPermissions"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import { useState } from "react"

const Create = () => {
  const queryClient = useQueryClient()
  const goBack = useGoBack();

  const [productSearch, setProductSearch] = useDebounce('')
  const [captureMethodSearch, setCaptureMethodSearch] = useDebounce('')
  const [feeTemplateOptionsSearch, setFeeTemplateOptionsSearch] = useDebounce('')

  const [messages, setMessages] = useState<string[] | boolean>(false);


  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateFeeTemplatePermission = !isUserPermissionsPending ?  hasUserPermission('Admin::Billing::FeeTemplates::Create') : true

  const { data: productOptions } = useProducts({
    filters: { search: productSearch },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: productSearch },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: captureMethodsOptions } = useBillingCaptureMethods({
    filters: { search: captureMethodSearch },
    mapper: Mapper.mapToOptions<BillingCaptureMethod>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: feeTemplateOptions } = useFeeTemplateOptions({
    filters: { search: feeTemplateOptionsSearch },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors }
  } = useForm<FeeTemplateValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const {
    mutate: createFeeTemplate,
    isPending: createFeeTemplateIsPending,
  } = useCreateFeeTemplate({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeTemplates'] })
      goBack({ fallback: '/fee-templates' })
    },
    onError: FormTransformers.errorsTransformer<FeeTemplateValidationSchema>({ setError, setMessages }),
  })

  const calculateAs = watch('calculate_as')
  const enableMinimumConsumption = watch('enableMinimumConsumption')
  const enableApplicableProducts = watch('enableApplicableProducts')
  const enableCaptureMethods = watch('enableApplicableCaptureMethods');

  const onSubmit: SubmitHandler<FeeTemplateValidationSchema> = (data) => {
    createFeeTemplate(data)
  }

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateFeeTemplatePermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/fee-templates" />
            <p className="text-white-400 font-normal">Tarifas</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>Tarifa Base</h2>
          </div>
          <div className="flex gap-4">
            <Link to='/fee-templates'><Button variant='outline-error'>Cancelar</Button></Link>
            <Button
              type="submit"
              data-testid="create_fee_template_button"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={createFeeTemplateIsPending}
            >
              {createFeeTemplateIsPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Criar Tarifa'}
            </Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(96vh-96px)] overflow-y-auto">
          <p className="text-lg font-bold">Detalhes da Tarifa</p>
          <TextField
            label="Nome"
            data-testid="display_name_input"
            errorMessage={errors?.display_name?.message || undefined}
            {...register('display_name')}
            disabled={createFeeTemplateIsPending}
            markAsRequired
          />
          <Checkbox
            label="Tarifa padrão (adicionada em todas condições comerciais para esse tipo de cliente)"
            labelClassName="italic text-xs"
            {...register('apply_by_default')}
            disabled={createFeeTemplateIsPending}
          />
          <Checkbox
            label="Não aplicar quando houver tarifa especial"
            labelClassName="italic text-xs"
            {...register('not_use_if_has_special_fee')}
            disabled={createFeeTemplateIsPending}
          />
          <div className="flex gap-2">
            <SelectController
              control={control}
              name='kind'
              dataTestid="kind_selector"
              label="Tipo de tarifa"
              className="w-1/4"
              errorMessage={errors.applicable_entity?.message}
              options={feeTemplateKindOptions}
              isDisabled={createFeeTemplateIsPending}
              markAsRequired
            />
            <SelectController
              control={control}
              name='applicable_entity'
              dataTestid="applicable_entity_selector"
              label="Aplicado em"
              className="w-1/4"
              errorMessage={errors.applicable_entity?.message}
              options={applicableEntityOptions}
              isDisabled={createFeeTemplateIsPending}
              markAsRequired
            />
            <SelectController
              control={control}
              name='payment_modality'
              dataTestid='payment_modality_selector'
              label="Modalidade de pagamento"
              className="w-1/4"
              errorMessage={errors.payment_modality?.message}
              options={paymentModalityOptions}
              isDisabled={createFeeTemplateIsPending}
              markAsRequired
            />
            <SelectController
              control={control}
              name='calculate_as'
              dataTestid='calculate_as_selector'
              label="Medida"
              className="w-1/4"
              errorMessage={errors.calculate_as?.message}
              options={calculateAsOptions}
              triggerOnChange={() => setValue('default_value', '')}
              isDisabled={createFeeTemplateIsPending}
              markAsRequired
            />
            {calculateAs?.value === 'percentage' ? (
              <MaskedTextField
                label="Valor padrão"
                data-testid="default_value_input"
                className="w-1/4"
                mask={percentageMaskAllowNegative}
                errorMessage={errors?.default_value?.message || undefined}
                {...register('default_value')}
                disabled={createFeeTemplateIsPending}
                markAsRequired
              />
            ) : (
              <Controller
                name="default_value"
                control={control}
                render={({ field }) => (
                  <CurrencyTextField
                    label="Valor padrão"
                    data-testid="default_value_input"
                    className="w-1/4"
                    maxValue={9_999.99}
                    errorMessage={errors?.default_value?.message || undefined}
                    disabled={createFeeTemplateIsPending}
                    {...field}
                    markAsRequired
                    allowNegative
                  />
                )}
              />
            )}
          </div>
          <SelectController
            control={control}
            name='option_id'
            dataTestid='option_id_selector'
            label="Aplicação"
            errorMessage={errors.option_id?.message}
            options={feeTemplateOptions}
            onInputChange={(e) => setFeeTemplateOptionsSearch(e)}
            isDisabled={createFeeTemplateIsPending}
            markAsRequired
          />
          <SelectController
            control={control}
            name='applicable_establishment_commercial_kinds'
            dataTestid='applicable_establishment_commercial_kinds_selector'
            label="Tipo de estabelecimento"
            defaultValue={applicableEstablishmentCommercialKindOptions.find((option) => option.value === 'all')}
            errorMessage={errors.applicable_establishment_commercial_kinds?.message}
            options={applicableEstablishmentCommercialKindOptions}
            isDisabled={createFeeTemplateIsPending}
          />
          <Accordion
            head={(
              <Checkbox
                label="Consumo mínimo"
                {...register('enableMinimumConsumption')}
                errorMessage={errors.enableMinimumConsumption?.message}
                disabled={createFeeTemplateIsPending}
              />
            )}
            body={(
              <Controller
                name="conditional_minimum_consumption"
                control={control}
                disabled={!enableMinimumConsumption || createFeeTemplateIsPending}
                render={({ field }) => (
                  <CurrencyTextField
                    label="Consumo mínimo"
                    errorMessage={errors?.conditional_minimum_consumption?.message || undefined}
                    {...field}
                    markAsRequired={enableMinimumConsumption}
                  />
                )}
              />
            )}
          />
          <Accordion
            head={(
              <Checkbox
                label="Produtos e serviços aplicáveis"
                errorMessage={errors.enableApplicableProducts?.message}
                {...register('enableApplicableProducts')}
                disabled={createFeeTemplateIsPending}
              />
            )}
            body={(
              <SelectController
                control={control}
                name="fee_template_applicable_products_attributes"
                label="Produtos e serviços aplicáveis"
                isMulti
                className="w-[99%] mx-auto overflow-visible"
                onInputChange={(e) => setProductSearch(e)}
                options={[...(productOptions?.map((option) => ({ ...option, type: 'Product' })) || []), ...(productCategoryOptions?.map((option) => ({ ...option, type: 'ProductCategory' })) || [])]}
                errorMessage={errors.fee_template_applicable_products_attributes?.message}
                isDisabled={!enableApplicableProducts || createFeeTemplateIsPending}
                markAsRequired={enableApplicableProducts}
              />
            )}
            contentClassName="data-[state=open]:overflow-visible"
          />

          <Accordion
            name="fee_template_applicable_capture_methods"
            head={(
              <Checkbox
                label="Métodos de capturas aplicáveis"
                errorMessage={errors.enableApplicableCaptureMethods?.message}
                {...register('enableApplicableCaptureMethods')}
                disabled={createFeeTemplateIsPending}
              />
            )}
            body={(
              <SelectController
                control={control}
                name="fee_template_applicable_capture_methods_attributes"
                label="Métodos de capturas aplicáveis"
                isMulti
                className="w-[99%] mx-auto overflow-visible"
                onInputChange={(e) => setCaptureMethodSearch(e)}
                options={captureMethodsOptions}
                errorMessage={errors.fee_template_applicable_capture_methods_attributes?.message}
                isDisabled={!enableCaptureMethods || createFeeTemplateIsPending}
                markAsRequired={enableCaptureMethods}
              />
            )}
            contentClassName="data-[state=open]:overflow-visible"
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={!!errors}
        />
      )}
    </ErrorMessageWrapper>
  )
}

export default Create
